/**
 * Frontend Tool Handlers — DocEngine 审查流水线
 *
 * 处理的工具：
 * - review_and_redline_word → Word 文书审查专员
 * - review_excel_data → Excel 数据审计专员
 */

import {
  registerFrontendTool,
  authedPost,
  triggerBase64Download,
  type FrontendToolContext,
} from '../frontend-dispatcher';
import { readFileAsBase64 } from '../../hooks/useFileSystem';

// ─── review_and_redline_word ────────────────
registerFrontendTool('review_and_redline_word', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath argument]`;

  ctx.setMessageStatus('searching', `📝 正在对 ${args.filePath} 进行 AI 深度法律审查...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read Word file ${args.filePath}]`;

  const reviewResponse = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/review-word-pipeline`,
    {
      fileBase64: fileData.base64,
      fileName: fileData.fileName,
      reviewFocus: args.reviewFocus || '全面法律审查',
    },
    ctx.authToken,
  );

  if (!reviewResponse.ok) {
    return `[Error: Word review pipeline failed: ${reviewResponse.statusText}]`;
  }

  const result = await reviewResponse.json();

  // 如果后端返回了审查版文件 Base64，触发浏览器下载
  if (result.reviewedFileBase64) {
    triggerBase64Download(
      result.reviewedFileBase64,
      result.mimeType,
      result.reviewedFileName || 'reviewed.docx',
    );
  }

  return `[Word 审查完成 ${args.filePath}]:\n` +
    `修改建议: ${result.modificationsApplied || 0} 条\n` +
    `批注: ${result.commentsAdded || 0} 条\n` +
    `流水线状态: ${result.pipeline || 'unknown'}\n` +
    `\nGemini 审查总结: ${result.geminiSummary || '无'}\n` +
    (result.reviewedFileBase64 ? '\n✅ 带红线修订的 Word 文件已自动下载。' : '\n⚠️ 红线回写失败，审查报告已返回文本形式。') +
    (result.paragraphsText ? `\n\n[解析数据供参考]:\n${result.paragraphsText}` : '');
});

// ─── review_excel_data ──────────────────────
registerFrontendTool('review_excel_data', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath argument]`;

  ctx.setMessageStatus('searching', `📊 正在对 ${args.filePath} 进行 AI 财务审计...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read Excel file ${args.filePath}]`;

  const reviewResponse = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/review-excel-pipeline`,
    {
      fileBase64: fileData.base64,
      fileName: fileData.fileName,
      reviewFocus: args.reviewFocus || '全面财务审计',
    },
    ctx.authToken,
  );

  if (!reviewResponse.ok) {
    return `[Error: Excel review pipeline failed: ${reviewResponse.statusText}]`;
  }

  const result = await reviewResponse.json();

  if (result.reviewedFileBase64) {
    triggerBase64Download(
      result.reviewedFileBase64,
      result.mimeType,
      result.reviewedFileName || 'audited.xlsx',
    );
  }

  return `[Excel 审计完成 ${args.filePath}]:\n` +
    `标红单元格: ${result.cellsHighlighted || 0} 个\n` +
    `批注: ${result.commentsAdded || 0} 条\n` +
    `流水线状态: ${result.pipeline || 'unknown'}\n` +
    `\nGemini 审计总结: ${result.geminiSummary || '无'}\n` +
    (result.reviewedFileBase64 ? '\n✅ 带标红批注的 Excel 文件已自动下载。' : '\n⚠️ 标红回写失败，审计报告已返回文本形式。') +
    (result.dataText ? `\n\n[解析数据供参考]:\n${result.dataText}` : '');
});
